import { useCallback, useRef, useState } from 'react';

type BrowserSpeechRecognition = any;
type BrowserSpeechRecognitionEvent = any;

export function useWebSpeechTranscript() {
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const [transcript, setTranscript] = useState<string>('');
  const committedRef = useRef<string>(''); // finalized words
  const stopRequestedRef = useRef<boolean>(false);
  const isRunningRef = useRef<boolean>(false);

  const stop = useCallback((options?: { markStopRequested?: boolean }) => {
    const markStopRequested = options?.markStopRequested ?? true;
    if (markStopRequested) {
      stopRequestedRef.current = true;
    }
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {
        /* ignore */
      }
    }
    recognitionRef.current = null;
    isRunningRef.current = false;
  }, []);

  const start = useCallback(() => {
    if (typeof window === 'undefined') return;
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;

    stopRequestedRef.current = false;
    // Clear any existing recognizer without toggling stopRequested back to true
    stop({ markStopRequested: false });

    const recognition: BrowserSpeechRecognition = new SpeechRecognitionCtor();
    recognitionRef.current = recognition;
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    isRunningRef.current = true;

    recognition.onresult = (event: BrowserSpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const text = res[0]?.transcript ?? '';
        if (res.isFinal) {
          committedRef.current = `${committedRef.current} ${text}`.trim();
        } else {
          interim = `${interim} ${text}`.trim();
        }
      }
      const combined = `${committedRef.current} ${interim}`.trim();
      setTranscript(combined);
    };

    recognition.onerror = () => {
      stop();
    };

    recognition.onend = () => {
      if (!isRunningRef.current || stopRequestedRef.current) return;
      // Browser may stop on silence; restart without clearing committed text
      try {
        recognition.start();
      } catch {
        /* ignore restart errors */
      }
    };

    try {
      recognition.start();
    } catch {
      stop();
    }
  }, [stop]);

  const reset = useCallback(() => {
    // Keep committed text unless explicitly cleared externally
    setTranscript(committedRef.current);
  }, []);

  const hardReset = useCallback(() => {
    committedRef.current = '';
    setTranscript('');
  }, []);

  return {
    transcript,
    start,
    stop,
    reset,
    hardReset,
  };
}
